import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { ChapterSummary } from '../../api/learnApi';
import { PrecisionNotesDownload } from '../dashboard/PrecisionNotesDownload';

/**
 * Feature 023 — the chapter tabs and the all-chapters tools, shared by the desktop rail
 * (`StudySidebar`) and the phone disclosure (`ChapterPicker`) so the two can never drift.
 */

export interface ChapterTabsProps {
  chapters: ChapterSummary[] | null;
  failed?: boolean;
  /** Slug of the chapter being read, highlighted as the current tab. Absent on the landing page. */
  activeSlug?: string;
  /** Stamped onto each link so the reader's Back control returns to where the tab was clicked. */
  originPath: string;
  /** Called after a tab is chosen — lets the phone disclosure close itself. */
  onNavigate?: () => void;
}

/** The chapter tabs themselves. Renders the same three fallbacks the old chapter list used. */
export function ChapterTabList({
  chapters,
  failed = false,
  activeSlug,
  originPath,
  onNavigate,
}: ChapterTabsProps) {
  if (failed) {
    return <p className="px-[22px] text-sm text-ink-soft">Unable to load chapters right now.</p>;
  }
  if (chapters === null) {
    return <p className="px-[22px] text-sm text-ink-soft">Loading chapters…</p>;
  }
  if (chapters.length === 0) {
    return <p className="px-[22px] text-sm text-ink-soft">No chapters available yet.</p>;
  }

  return (
    <ul className="m-0 list-none p-0">
      {chapters.map((ch) => {
        const isActive = ch.slug === activeSlug;
        return (
          <li key={ch.slug}>
            <Link
              to={`/learn/${ch.slug}`}
              state={{ from: originPath, fromLabel: 'Learn' }}
              aria-current={isActive ? 'page' : undefined}
              onClick={onNavigate}
              // min-h-[44px] keeps the AAA touch target on portrait tablets, which take this
              // desktop layout; the design's 9px padding alone would land at ~36px.
              className={
                'flex min-h-[44px] items-center border-l-[3px] px-[22px] py-[9px] text-[13.5px] ' +
                'leading-snug no-underline focus:outline-none focus-visible:ring-2 ' +
                'focus-visible:ring-inset focus-visible:ring-primary-500 ' +
                (isActive
                  ? 'border-brass bg-[#fff8e6] font-bold text-primary-600'
                  : 'border-transparent text-ink hover:bg-[#fff8e6]')
              }
            >
              Chapter {ch.order} - {ch.title}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Search + bulk downloads — the actions that span every chapter, not just the open one. */
export function ChapterTools({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <Link
        to="/learn/search"
        onClick={onNavigate}
        className="flex min-h-[44px] items-center gap-2 px-[22px] py-1.5 text-[13.5px] text-ink no-underline hover:bg-[#fff8e6] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
      >
        <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-ink-soft" aria-hidden="true" />
        Search the notes
      </Link>
      <PrecisionNotesDownload />
    </>
  );
}
